import { makeStyles } from '@material-ui/core/styles'
import { getCoachAvatarFontSize } from 'components/coach-user-avatar'
import { CALLOUT_BACKGROUND, CALLOUT_FOREGROUND } from 'lib/form-field-styles'
import { interactiveCardHoverStyles } from 'lib/styles/interactive-card-hover'

export const useStyles = makeStyles(theme => ({
  section: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    marginBottom: theme.spacing(1),
  },
  coachGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    width: '100%',
    minWidth: 0,
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  coachGridSingle: {
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      maxWidth: 560,
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    boxShadow: 'none',
    ...interactiveCardHoverStyles(theme),
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5),
    flex: '1 1 auto',
    minHeight: 0,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  cardBodyClickable: {
    cursor: 'pointer',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  avatar: {
    width: 40,
    height: 40,
    fontSize: getCoachAvatarFontSize(40),
    fontWeight: 500,
    color: '#00205B',
    backgroundColor: '#E8F1FF',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  details: {
    minWidth: 0,
  },
  name: {
    fontSize: '1.125rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#00205B',
    margin: 0,
  },
  locationRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    marginTop: 'auto',
    minWidth: 0,
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  locationSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: 0,
    flex: '1 1 auto',
    minWidth: 0,
  },
  locationSectionIcon: {
    fontSize: 18,
    color: '#6B7C87',
    marginTop: 2,
    flexShrink: 0,
  },
  locationSectionCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  facilityName: {
    fontSize: '0.875rem',
    fontWeight: 700,
    lineHeight: 1.4,
    color: '#00205B',
    margin: 0,
  },
  facilityNameLink: {
    fontSize: '0.875rem',
    fontWeight: 700,
    lineHeight: 1.4,
    color: '#00205B',
    margin: 0,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  locationLine: {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: '#6B7C87',
    margin: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
    },
  },
  borderedActionButton: {
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'color'],
      { duration: theme.transitions.duration.short },
    ),
    '&:hover': {
      backgroundColor: '#EBEBEB',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  primaryActionButton: {
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    backgroundColor: theme.palette.blue.main,
    color: theme.palette.common.white,
    boxShadow: 'none',
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: '#0046D6',
      boxShadow: 'none',
      color: theme.palette.common.white,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  packFooters: {
    display: 'flex',
    flexDirection: 'column',
  },
  packFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2.5),
    backgroundColor: CALLOUT_BACKGROUND,
    borderTop: '1px solid #E0E0E0',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  packCopy: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  packIcon: {
    color: CALLOUT_FOREGROUND,
    flexShrink: 0,
    fontSize: 18,
  },
  packText: {
    margin: 0,
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: CALLOUT_FOREGROUND,
  },
  packAction: {
    minWidth: 0,
    padding: 0,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    color: CALLOUT_FOREGROUND,
    backgroundColor: 'transparent',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'none',
    },
    '&:hover $packActionLabel': {
      textDecoration: 'underline',
    },
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(3.25),
    },
  },
  packActionLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  packActionIcon: {
    fontSize: 14,
    width: 14,
    height: 14,
  },
}))
